import React, { useEffect, useState, useMemo, useRef } from 'react';
import { Alert, Box, Button, Grid } from '@mui/material';
import moment from 'moment';

import SelectAirport from '../components/SelectAirport';
import IncDecInput from '../components/IncDecInput';
import { AirportModal } from '../components/AirportModal';
import { FuelModal } from '../components/FuelModal';
import {
  timeConvert,
  timeConvert_Floor,
  hoursToMinutes,
} from '../utilities/helperfunctions';
import '../style/fplfuel.scss';
import {
  calculateReqFuel,
  calculateIncreasedTripFuelKg,
  distributeFuelReduction,
  getTankeringFuelKg,
  isTankeringFuel,
} from '../helpers';
import {
  getContingencyAirportInfo,
  isSelectedAirportContingency,
} from '../helpers/contingencyAirportHelpers';
import { useDataServiceContext } from '../hooks/useDataServiceContext';

function firstString(value) {
  if (value == null) return '';
  if (Array.isArray(value)) return value[0] ?? '';
  return String(value);
}

/** TODO: REMOVE. FD Pro: show only before local midnight 20 Apr 2026. */
function isFdProTransitionActive() {
  const fdProEnd = new Date(2026, 3, 20);
  return Date.now() < fdProEnd.getTime();
}

export function FplFuel(props) {
  const { getNOTAMByICAO, flightData, dismissOfpFuelResetNotice } =
    useDataServiceContext();
  const [isAirportModalOpen, setIsAirportModalOpen] = useState(false);
  const [isFuelModalOpen, setIsFuelModalOpen] = useState(false);
  const [selectedAirport, setSelectedAirport] = useState();
  const [alternateAirport, setAlternateAirport] = useState(
    props.data?.Alt_List?.[0]?.Alternate?.[0]
  );
  const [coldSoak, setColdSoak] = useState(props?.coldSoak ?? '');
  const [adjustments, setAdjustments] = useState(props?.adjustments ?? '');
  const [contingencyWeather, setContingencyWeather] = useState({
    metar: null,
    taf: null,
  });

  const tankeringValue = getTankeringFuelKg(props.data);

  useEffect(() => {
    setAlternateAirport(props?.data?.Alt_List?.[0]?.Alternate?.[0]);
  }, [props.data]);

  useEffect(() => {
    setColdSoak(props?.coldSoak ?? '');
  }, [props.coldSoak]);

  useEffect(() => {
    setAdjustments(props?.adjustments ?? '');
  }, [props.adjustments]);

  const handleCloseFuelModal = () => {
    setIsFuelModalOpen(false);
  };

  const standardRequestedFuelTon = calculateReqFuel(
    props.data?.nBlockFuel_KG,
    props.data?.Alt_List?.[0]?.Alternate?.[0],
    alternateAirport
  );

  const parseFuelTonnes = (value) => {
    const n = Number.parseFloat(String(value ?? "").replace(",", "."));
    return Number.isFinite(n) ? n : 0;
  };

  const standardTonnes = parseFuelTonnes(standardRequestedFuelTon);
  const requestedTonnes = parseFuelTonnes(props.reqFuel);

  const handleResetRequestedFuel = () => {
    props.onChangeReqFuel(standardRequestedFuelTon);
  };

  const handleSelectAlternateAirport = (alternateAirport) => {
    const updatedReqFuel = calculateReqFuel(
      props.data?.nBlockFuel_KG,
      props.data?.Alt_List?.[0].Alternate?.[0],
      alternateAirport
    );
    props.onChangeReqFuel(updatedReqFuel);
    setAlternateAirport(alternateAirport);
  };

  const onChangeActiveAirport = (airport) => {
    setSelectedAirport(airport);
    setIsAirportModalOpen(true);
  };

  const navigate = (url) => {
    window.location.href = url;
  };


  const extraFuelTonnesDelta = requestedTonnes - standardTonnes;
  const extraFuel = extraFuelTonnesDelta.toFixed(1);
  const hasAdjustedRequestedFuel = Math.abs(extraFuelTonnesDelta) >= 0.05;
  const extraFuelDeltaKg = extraFuelTonnesDelta * 1000;
  const averageFuelFlow = props.data?.nAverageFF_KGH ?? 1;
  const extraHours = extraFuelDeltaKg / averageFuelFlow;
  const extraFuelKg = Math.round(extraFuelDeltaKg);

  const { tankeringReductionKg, discretionaryKg } = distributeFuelReduction(
    extraFuelKg,
    tankeringValue
  );

  const extra = [];
  if (props.data?.ExtraFuelList?.[0]?.ExtraFuel)
    props.data.ExtraFuelList[0].ExtraFuel.forEach((item) => {
      const reductionKg = isTankeringFuel(item) ? tankeringReductionKg : 0;
      extra.push({
        name: item?.sExtra_FuelReason,
        value: Number(firstString(item?.nExtra_Fuel_KG)) - reductionKg,
        hours:
          Number(firstString(item?.dbExtra_FuelDur_H)) -
          reductionKg / averageFuelFlow,
      });
    });

  const increasedTripFuelKg = calculateIncreasedTripFuelKg(
    extraFuelKg,
    props.data?.dbTripFuelDur_H
  );

  // Get contingency airport information
  const {
    contLabel,
    contingencyAirportICAO,
    isInAirportsList: isContingencyInAirportsList,
    contingencyAirportObject,
    isAlsoAlternate: isContingencyAlsoAlternate,
    airportsWithContingency,
    contingencyAirportICAOForStyling,
  } = getContingencyAirportInfo(props.data, props.airports);

  const contingencyAirportICAORef = useRef(contingencyAirportICAO);
  const isFetchingRef = useRef(false);

  const contingencyShouldBeClickable =
    contLabel === 'CONT 3% + ERA' &&
    contingencyAirportICAO &&
    !isContingencyInAirportsList &&
    contingencyAirportObject;

  // Check if selected airport is the contingency airport
  const isSelectedAirportContingencyCheck = isSelectedAirportContingency(
    selectedAirport,
    contingencyAirportICAO,
    contLabel
  );

  // Fetch weather data for contingency airport if needed
  useEffect(() => {
    const fetchContingencyWeather = async () => {
      if (isFetchingRef.current && contingencyAirportICAORef.current === contingencyAirportICAO) {
        return;
      }

      if (
        contLabel === 'CONT 3% + ERA' &&
        contingencyAirportICAO &&
        !isContingencyInAirportsList &&
        contingencyAirportObject
      ) {
        isFetchingRef.current = true;
        contingencyAirportICAORef.current = contingencyAirportICAO;

        try {
          const notamData = await getNOTAMByICAO(contingencyAirportICAO);

          if (notamData) {
            setContingencyWeather({
              metar: notamData.metar || null,
              taf: notamData.taf || null,
            });
          } else {
            setContingencyWeather({ metar: null, taf: null });
          }
        } catch {
          console.error("Error fetching contingency airport weather");
          setContingencyWeather({ metar: null, taf: null });
        } finally {
          isFetchingRef.current = false;
        }
      } else {
        // Reset when contingency airport is no longer relevant
        setContingencyWeather({ metar: null, taf: null });
        isFetchingRef.current = false;
        contingencyAirportICAORef.current = null;
      }
    };

    fetchContingencyWeather();
  }, [contLabel, contingencyAirportICAO, isContingencyInAirportsList]);

  const combinedMetar = useMemo(() => {
    if (contingencyWeather.metar) {
      return [...(props.metar || []), contingencyWeather.metar];
    }
    return props.metar || [];
  }, [props.metar, contingencyWeather.metar]);

  const combinedTaf = useMemo(() => {
    if (contingencyWeather.taf) {
      return [...(props.taf || []), contingencyWeather.taf];
    }
    return props.taf || [];
  }, [props.taf, contingencyWeather.taf]);

  const hasArrivalAlternates = props.data?.Alt_List?.[0]?.Alternate?.some(
    (alt) => alt?.sADFunction?.[0] && alt.sADFunction[0] !== 'ApprovedAlternate'
  ) ?? false;

  const isApprovedAlternate = alternateAirport?.sADFunction?.[0] === 'ApprovedAlternate';
  const shouldShowNilValues = !hasArrivalAlternates;
  const shouldShowApprovedAlternate = hasArrivalAlternates && isApprovedAlternate;

  const getAlternateValue = () => {
    if (shouldShowNilValues) return 0;
    if (isApprovedAlternate) return null;
    return alternateAirport?.nAlternateFuel_KG ?? 0;
  };

  const getAlternateHours = () => {
    if (shouldShowNilValues) return 0;
    if (isApprovedAlternate) return null;
    return alternateAirport?.dbAlternateFuelDur_H ?? 0;
  };

  const getAlternateAirport = () => {
    if (shouldShowNilValues) return 'NIL';
    return alternateAirport?.sADICAO ?? 'NIL';
  };

  const fuelSection1 = [
    {
      name: 'TRIP',
      value: Number(props.data?.nTripFuel_KG ?? 0) + increasedTripFuelKg,
      hours: props.data?.dbTripFuelDur_H ?? 0,
      airport: props.data?.sDestICAO?.[0] ?? '',
    },
    {
      name: contLabel,
      value: props.data?.nContingencyFuel_KG ?? 0,
      hours: props.data?.dbContingencyDur_H ?? 0,
      airport: contingencyAirportICAO,
      isContingencyClickable: contingencyShouldBeClickable,
      contingencyAirportObject: contingencyAirportObject,
    },
    {
      name: 'ALTN',
      value: getAlternateValue(),
      hours: getAlternateHours(),
      airport: getAlternateAirport(),
      isApprovedAlternate: shouldShowApprovedAlternate,
    },
    {
      name: 'FINRES',
      value: props.data?.nFinalReserve_KG ?? 0,
      hours: props.data?.dbFinalReserveDur_H ?? 0,
    },
    ...extra,
    {
      name: 'DISCR',
      value: discretionaryKg,
      hours: discretionaryKg / averageFuelFlow,
    },
  ];

  const tkofFuel = fuelSection1.reduce((a, b) => {
    const reducedValue = a + Number(b.value);
    return reducedValue;
  }, 0);
  const tkofHours = fuelSection1.reduce((a, b) => {
    const reducedHours = a + Number(b.hours);
    return reducedHours;
  }, 0);

  const fuelSection2 = [
    {
      name: 'T/OFF FUEL',
      value: tkofFuel,
      hours: tkofHours,
    },
    {
      name: 'TAXI',
      value: props.data?.nTaxiFuel_KG ?? 0,
      hours: props.data?.dbTaxiFuelDur_H ?? 0,
      airport: props.data?.sDepICAO?.[0] ?? '',
    },
  ];

  const getAlternateAirportFuelDifference = (alternateAirport) => {
    return (
      (alternateAirport?.nAlternateFuel_KG?.[0] ?? 0) -
      (props.data?.Alt_List?.[0]?.Alternate?.[0]?.nAlternateFuel_KG?.[0] ?? 0)
    );
  };

  const getAlternateAirportHourDifference = (alternateAirport) => {
    return (
      (alternateAirport?.dbAlternateFuelDur_H?.[0] ?? 0) -
      (props.data?.Alt_List?.[0]?.Alternate?.[0]?.dbAlternateFuelDur_H?.[0] ??
        0)
    );
  };

  const landingFuel =
    parseInt(props.data?.nArrivalFuel_KG ?? '0') +
    getAlternateAirportFuelDifference(alternateAirport) +
    extraFuelKg -
    increasedTripFuelKg;
  const landingHours = props.data?.dbArrivalFuelDur_H?.[0]
    ? Number(props.data.dbArrivalFuelDur_H[0]) +
    getAlternateAirportHourDifference(alternateAirport) +
    extraHours
    : 0;

  const baseBlockFuelFuel = props.data?.nBlockFuel_KG?.[0]
    ? parseInt(props.data?.nBlockFuel_KG?.[0] ?? 0) + extraFuelKg
    : 0;

  const baseBlockFuelHours = props.data?.dbBlockFuelDur_H?.[0]
    ? Number(props.data.dbBlockFuelDur_H[0]) + extraHours
    : 0;

  const fuelSection3 = [
    {
      name: 'BLOCKFUEL',
      value:
        baseBlockFuelFuel + getAlternateAirportFuelDifference(alternateAirport),
      hours:
        baseBlockFuelHours +
        getAlternateAirportHourDifference(alternateAirport),
      airport: props.data?.sDestICAO?.[0] ?? '',
    },
    {
      name: 'LANDING',
      value: landingFuel,
      hours: landingHours,
    },
  ];

  const fuelSections = [fuelSection1, fuelSection2, fuelSection3];

  const ezfmValue = props.data?.nEstZFW_KG ?? 0;
  const ezfmMax = props.data?.nStructuralLimitZFW_KG ?? 0;
  const ezfmExtra = (ezfmMax - ezfmValue) / 1000;

  const etomValue =
    parseInt(props.data?.nEstTOW_KG ?? '0') +
    getAlternateAirportFuelDifference(alternateAirport) +
    extraFuelKg;
  const etomMax = Number(props.data?.nStructuralLimitTOW_KG ?? 0);
  const etomExtra = (etomMax - etomValue) / 1000;

  const elmValue =
    parseInt(props.data?.nEstLW_KG ?? '0') +
    getAlternateAirportFuelDifference(alternateAirport) +
    extraFuelKg -
    increasedTripFuelKg;
  const elmMax = Number(props.data?.nStructuralLimitLW_KG ?? 0);
  const elmExtra = (elmMax - elmValue) / 1000;

  const ezfm = [
    {
      name: 'EZFM',
      value: ezfmValue,
      max: ezfmMax,
      extra: ezfmExtra,
    },
    {
      name: 'ETOM',
      value: etomValue,
      max: etomMax,
      extra: etomExtra,
    },
    {
      name: 'ELM',
      value: elmValue,
      max: elmMax,
      extra: elmExtra,
    },
  ];

  const depTime = moment(props.data?.dtSTD?.[0]);
  const arrTime = moment(props.data?.dtSTA?.[0]);
  const flightDuration = moment.duration(arrTime.diff(depTime)).asMinutes();
  let bt = Math.floor(
    (props.data?.dbEstBlockTime?.[0] ?? 0) * 60 - flightDuration
  );
  if (bt > 0) bt = '+' + bt;

  const fuelGainLossTankeringLabel = props.data?.sGainLoss;

  return (
    <div>
      <Grid container>
        <Grid item xs={5} sx={{ display: 'flex', alignItems: 'stretch' }}>
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1.25,
            }}
          >
            <p>
              Trip{' '}
              <span style={{ fontWeight: 800 }}>
                {props.data?.dbEstFlightTime
                  ? timeConvert(props.data.dbEstFlightTime)
                  : 'N/A'}
              </span>
            </p>
            <p>
              Block{' '}
              <span style={{ fontWeight: 800 }}>
                {props.data?.dbEstBlockTime
                  ? timeConvert(props.data.dbEstBlockTime)
                  : 'N/A'}
              </span>
              <span
                style={{
                  color: bt > 0 ? 'var(--norwegian-red)' : 'var(--green)',
                  fontSize: '13px',
                  fontWeight: 800,
                }}
              >
                {' '}
                ({bt})
              </span>
            </p>
            <p>
              PAX{' '}
              <span style={{ fontWeight: 800 }}>
                {props.data?.sPax ?? 'N/A'}
              </span>
            </p>
          </Box>
        </Grid>
        <Grid item xs={7}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              backgroundColor: '#F0F0F0',
              width: '100%',
            }}
          >
            {flightData.ofpFuelResetNotice && (
              <Alert
                severity='info'
                onClose={dismissOfpFuelResetNotice}
                sx={{
                  width: '100%',
                  borderRadius: 0,
                  px: 0.75,
                  py: 0.25,
                  alignItems: 'flex-start',
                  '& .MuiAlert-icon': {
                    p: 0,
                    mr: 0.75,
                    py: 0.125,
                    fontSize: 16,
                    opacity: 1,
                  },
                  '& .MuiAlert-message': {
                    p: 0,
                    fontSize: 11,
                    fontWeight: 600,
                    lineHeight: 1.25,
                  },
                  '& .MuiAlert-action': {
                    p: 0,
                    pl: 0.25,
                    mr: -0.25,
                    mt: -0.25,
                    alignItems: 'flex-start',
                  },
                  '& .MuiIconButton-root': {
                    p: 0.25,
                  },
                }}
              >
                {flightData.ofpFuelResetNotice}
              </Alert>
            )}
            <p style={{ padding: '8px' }}>Requested fuel</p>
            <IncDecInput
              value={props.reqFuel}
              onChange={props.onChangeReqFuel}
            />
              <Button
                size='small'
                variant='text'
              disabled={!hasAdjustedRequestedFuel}
                onClick={handleResetRequestedFuel}
                sx={{ mt: 0.25, textTransform: 'none' }}
              >
                Reset fuel
              </Button>
            <Box
              style={{
                display: 'flex',
                justifyContent: 'space-evenly',
                marginTop: '8px',
                width: '100%',
              }}
            >
              <p>Extra:</p>
              <p style={{ fontWeight: 600 }}>{extraFuel} T</p>
              <p>({hoursToMinutes(extraHours)} min)</p>
            </Box>
            {tankeringValue > 0 && (
              <p
                style={{
                  marginBottom: '8px',
                  color:
                    props.data?.sGainLoss[0][0] === '-'
                      ? 'var(--norwegian-red)'
                      : 'var(--green)',
                  fontWeight: 600,
                }}
              >
                {fuelGainLossTankeringLabel}
              </p>
            )}
          </Box>
        </Grid>
      </Grid>
      <AirportModal
        isOpen={isAirportModalOpen}
        setIsOpen={setIsAirportModalOpen}
        handleSelectAlternateAirport={handleSelectAlternateAirport}
        airport={selectedAirport}
        taf={combinedTaf}
        metar={combinedMetar}
        snowtam={props.snowtam}
        flightData={props.data}
        blockFuelFuel={
          baseBlockFuelFuel + getAlternateAirportFuelDifference(selectedAirport)
        }
        blockFuelHours={
          baseBlockFuelHours +
          getAlternateAirportHourDifference(selectedAirport)
        }
        adList={
          props?.tkofSettings?.ADList && props?.tkofSettings?.ADList?.length > 0
            ? props?.tkofSettings?.ADList[0]?.string
            : []
        }
        isContingencyAirport={isSelectedAirportContingencyCheck}
      />
      <SelectAirport
        airports={airportsWithContingency}
        activeAirport={props.airportSelected}
        onChangeActiveAirport={onChangeActiveAirport}
        reqFuel={props.reqFuel}
        blockFuel={props.data?.nBlockFuel_KG}
        tankeringValue={tankeringValue}
        contingencyAirportICAO={contingencyAirportICAOForStyling}
      />
      <Box className={'content-container'}>
        <Grid container direction='column' spacing={2}>
          {fuelSections.map((section, index) => (
            <Grid container item key={index} direction='column'>
              {section.map((fuel, index) => (
                <Grid
                  container
                  item
                  direction='row'
                  key={index}
                  spacing={1}
                >
                  <Grid item xs={5}>
                    <p style={{ overflowWrap: 'break-word' }}>{fuel.name}</p>
                  </Grid>
                  <Grid item xs={4}>
                    <p style={{ fontWeight: 600, textAlign: 'end' }}>
                      {fuel.isApprovedAlternate || fuel.value === null ? 'N/A' : `${fuel.value} kg`}
                    </p>
                  </Grid>
                  <Grid item xs={2}>
                    {fuel.isContingencyClickable && fuel.contingencyAirportObject ? (
                      <p
                        style={{
                          cursor: 'pointer',
                          color: 'blue',
                          textDecoration: 'underline',
                        }}
                        onClick={() => onChangeActiveAirport(fuel.contingencyAirportObject)}
                      >
                        {fuel.airport}
                      </p>
                    ) : (
                      <p>{fuel.airport}</p>
                    )}
                  </Grid>
                  <Grid item xs={1}>
                    <p
                      style={{
                        opacity: 0.5,
                        fontWeight: 600,
                        textAlign: 'end',
                      }}
                    >
                      {fuel.isApprovedAlternate || fuel.hours === null ? 'N/A' : timeConvert_Floor(fuel.hours)}
                    </p>
                  </Grid>
                </Grid>
              ))}
            </Grid>
          ))}
        </Grid>
        <FuelModal
          isOpen={isFuelModalOpen}
          handleClose={handleCloseFuelModal}
          coldSoak={coldSoak}
          adjustments={adjustments}
        />
        <Box className={'content-container'}>
          <Grid item alignSelf={'center'}>
            <Button
              onClick={() => setIsFuelModalOpen(!isFuelModalOpen)}
              variant='contained'
              className='fd-pro-button'
            >
              ADDITIONAL FUEL CONSIDERATIONS
            </Button>
          </Grid>
        </Box>
        {ezfm.map((item, index) => (
          <Grid container style={{ marginTop: '16px' }} spacing={1} key={index}>
            <Grid item xs={5}>
              <p style={{ fontSize: '20px', fontWeight: 800 }}>{item.name}</p>
              <p>Max: {item.max}</p>
            </Grid>
            <Grid item xs={3.5}>
              <p style={{ fontSize: '20px', fontWeight: 800 }}>{item.value}</p>
              <p>kg</p>
            </Grid>
            <Grid item xs={3.5}>
              {item.extra < 0 && item.extra > -1 && (
                <p
                  style={{
                    fontSize: '18px',
                    fontWeight: 800,
                    textAlign: 'end',
                    color: 'var(--norwegian-red)',
                  }}
                >
                  {parseFloat(item.extra).toPrecision(1)} T
                </p>
              )}
              {item.extra <= -1 && item.extra > -10 && (
                <p
                  style={{
                    fontSize: '18px',
                    fontWeight: 800,
                    textAlign: 'end',
                    color: 'var(--norwegian-red)',
                  }}
                >
                  {parseFloat(item.extra).toPrecision(2)} T
                </p>
              )}
              {item.extra <= -10 && (
                <p
                  style={{
                    fontSize: '18px',
                    fontWeight: 800,
                    textAlign: 'end',
                    color: 'var(--norwegian-red)',
                  }}
                >
                  {parseFloat(item.extra).toPrecision(3)} T
                </p>
              )}
              {item.extra >= 0 && item.extra < 1 && (
                <p
                  style={{
                    fontSize: '18px',
                    fontWeight: 800,
                    textAlign: 'end',
                    color: 'var(--green)',
                  }}
                >
                  + {parseFloat(item.extra).toPrecision(1)} T
                </p>
              )}
              {item.extra >= 1 && item.extra < 10 && (
                <p
                  style={{
                    fontSize: '18px',
                    fontWeight: 800,
                    textAlign: 'end',
                    color: 'var(--green)',
                  }}
                >
                  + {parseFloat(item.extra).toPrecision(2)} T
                </p>
              )}
              {item.extra >= 10 && (
                <p
                  style={{
                    fontSize: '18px',
                    fontWeight: 800,
                    textAlign: 'end',
                    color: 'var(--green)',
                  }}
                >
                  + {parseFloat(item.extra).toPrecision(3)} T
                </p>
              )}
            </Grid>
          </Grid>
        ))}

        <Grid
          container
          style={{ marginTop: '16px' }}
          spacing={4}
          direction='column'
        >
          <Grid container item spacing={2} direction='column'>
            <Grid item>
              <p>ROUTE ID: {props.data?.sRouteInfoOptimization ?? 'N/A'}</p>
              <p>{props.data?.sRouteDescription}</p>
            </Grid>
            <Grid item>
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1.5,
                  alignItems: 'center',
                }}
              >
                {isFdProTransitionActive() &&
                  firstString(props.data?.sFDProUrl) && (
                <Button
                      onClick={() =>
                        navigate(firstString(props.data.sFDProUrl))
                      }
                  variant='contained'
                  className='fd-pro-button'
                >
                  FD Pro
                </Button>
              )}
                {firstString(props.data?.sLidoUrl) && (
                  <Button
                    onClick={() => navigate(firstString(props.data.sLidoUrl))}
                    variant='contained'
                    className='fd-pro-button'
                  >
                    mPilot
                  </Button>
                )}
              </Box>
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </div>
  );
}
